import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import { TApi, TRoutes } from '../../../../shared/const'

import Loading from '../../../../UI/Loading'
import styles from './style.module.scss'

/*
 * Detailansicht einer Dokumentvorlage: blättert durch bis zu 10 echte, rollen-/
 * typpassende Datensätze und rendert die AKTUELLE Vorlage live als PDF (ansehen
 * oder herunterladen). Keine Bearbeitung — Dokumentvorlagen sind Ansichts-only.
 */
export default function DocumentDetail({ items, documentKey, onBack }) {
	const { t } = useTranslation()

	const entry = items.find(i => i.key === documentKey)

	const [samples, setSamples] = useState(null)
	const [sampleIndex, setSampleIndex] = useState(0)
	const [busy, setBusy] = useState(false)

	useEffect(() => {
		let active = true
		;(async () => {
			try {
				const { data } = await http.get(TApi.ADMIN_COMM_DOCUMENT_SAMPLES(documentKey), { disableToast: true })
				if (active) setSamples(data.samples || [])
			} catch (error) {
				if (active) setSamples([])
			}
		})()
		return () => {
			active = false
		}
	}, [documentKey])

	const current = samples && samples.length ? samples[sampleIndex] : null

	// PDF live rendern und als Blob holen; danach ansehen oder herunterladen.
	const withPdf = useCallback(
		async action => {
			if (!current) return
			setBusy(true)
			try {
				const { data } = await http.get(TApi.ADMIN_COMM_DOCUMENT_RENDER(documentKey, current.id), {
					responseType: 'blob',
					disableToast: true
				})
				const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
				if (action === 'download') {
					const a = document.createElement('a')
					a.href = url
					a.download = `${documentKey}-${current.id}.pdf`
					document.body.appendChild(a)
					a.click()
					a.remove()
				} else {
					window.open(url, '_blank', 'noopener')
				}
				// Object-URL nach kurzer Zeit freigeben (Tab/Download hat sie dann gelesen).
				setTimeout(() => URL.revokeObjectURL(url), 60000)
			} catch (error) {
				notifyError(error, 'PDF could not be generated')
			} finally {
				setBusy(false)
			}
		},
		[current, documentKey]
	)

	if (!entry) return null

	return (
		<div className={styles.detail}>
			<div className={styles.detailHeader}>
				<button type="button" className={styles.backBtn} onClick={onBack}>
					← {t('Back to overview')}
				</button>
				{entry.isStatic && <span className={styles.badgeInactive}>{t('Static')}</span>}
			</div>

			<h2 className={styles.detailTitle}>{entry.title}</h2>

			<div className={styles.infoGrid}>
				<div>
					<span className={styles.infoLabel}>{t('Target group')}</span>
					<div className={styles.targetGroups}>
						{entry.targetGroups.map(g => (
							<span key={g} className={styles.targetGroup}>
								{g}
							</span>
						))}
					</div>
				</div>
				{entry.companyTypes?.length > 0 && (
					<div>
						<span className={styles.infoLabel}>{t('Company types')}</span>
						<p className={styles.infoTrigger}>{entry.companyTypes.join(' · ')}</p>
					</div>
				)}
				<div>
					<span className={styles.infoLabel}>{t('Triggered by')}</span>
					<p className={styles.infoTrigger}>{entry.trigger}</p>
				</div>
			</div>

			<div className={styles.liveNotice}>
				<strong>{t('Live preview')}:</strong>{' '}
				{t('The current template is rendered with real (neutralized) production data.')}
			</div>

			<p className={styles.relatedLine}>
				<Link className={styles.link} to={`${TRoutes.HANDBOOK}?kapitel=dokumente`}>
					{t('In the handbook: Documents & certificates')}
				</Link>
			</p>

			{samples === null ? (
				<Loading />
			) : samples.length === 0 ? (
				<p className={styles.changelogEmpty}>{t('No matching production data found.')}</p>
			) : (
				<div className={styles.docPreview}>
					<div className={styles.previewHead}>
						<span className={styles.fieldLabel}>{t('Production data record')}</span>
						{samples.length > 1 && (
							<div className={styles.sampleNav}>
								<button
									type="button"
									className={styles.sampleNavBtn}
									onClick={() => setSampleIndex(i => (i - 1 + samples.length) % samples.length)}
								>
									‹
								</button>
								<span>
									{t('Record')} {sampleIndex + 1}/{samples.length}
								</span>
								<button
									type="button"
									className={styles.sampleNavBtn}
									onClick={() => setSampleIndex(i => (i + 1) % samples.length)}
								>
									›
								</button>
							</div>
						)}
					</div>

					<div className={styles.docRecordLabel}>{current?.label}</div>

					<div className={styles.actions}>
						<button type="button" className={styles.btnPrimary} disabled={busy} onClick={() => withPdf('view')}>
							{busy ? t('Generating...') : t('View PDF')}
						</button>
						<button type="button" className={styles.btnGhost} disabled={busy} onClick={() => withPdf('download')}>
							{t('Download PDF')}
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

DocumentDetail.propTypes = {
	items: PropTypes.array.isRequired,
	documentKey: PropTypes.string.isRequired,
	onBack: PropTypes.func.isRequired
}
